import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SpecModal from './SpecModal';

const pipeSubTypesStatic = [
  { id: 'seamless', nameEn: 'SEAMLESS', spec: 'DN15 ~ DN600 | ASTM A106 Gr.B / A53 / KS D3562', img: '/images/seamless.jpeg' },
  { id: 'pressure', nameEn: 'PRESSURE PIPE', spec: 'DN15 ~ DN500 | KS D3562 / ASTM A106 / ASME B36.10', img: '/images/pressure.jpg' },
  { id: 'general', nameEn: 'GENERAL PIPE', spec: 'DN15 ~ DN900 | KS D3507 / KS D3595 / KS D3562', img: '/images/general.jpg' },
  { id: 'structural', nameEn: 'STRUCTURAL', spec: 'φ21.7 ~ φ558.8 | KS D3566 / ASTM A500', img: '/images/structural.jpg' },
  { id: 'stainless', nameEn: 'STAINLESS', spec: 'DN15 ~ DN600 | ASTM A312 / KS D3576', img: '/images/stainless.jpg' },
  { id: 'alloy', nameEn: 'ALLOY STEEL', spec: 'DN15 ~ DN600 | ASTM A335 P5~P91 / A213', img: '/images/alloy.jpg' },
];

const productsStatic = [
  { id: 'pipe', nameEn: 'PIPE', hasSub: true, spec: '', img: '' },
  { id: 'fitting', nameEn: 'FITTING', hasSub: false, spec: '1/2" ~ 48" | BW / SW / THD', img: '/images/fitting.png' },
  { id: 'flange', nameEn: 'FLANGE', hasSub: false, spec: '150# ~ 2500# | ANSI B16.5 / KS', img: '/images/flange.jpg' },
  { id: 'pile', nameEn: 'PILE', hasSub: false, spec: 'φ300 ~ φ1200 | KS F 4602 / KS D3565', img: '/images/pile.jpeg' },
  { id: 'pe-coating', nameEn: 'PE COATING', hasSub: false, spec: '3LPE / 2LPE | KS D3589 / KS D3607 / DIN 30670', img: '/images/pe-coating.jpeg' },
];

function getProductNameKey(id: string) {
  const map: Record<string, string> = {
    pipe: 'prod_pipe_name',
    fitting: 'prod_fitting_name',
    flange: 'prod_flange_name',
    pile: 'prod_pile_name',
    'pe-coating': 'prod_pe_coating_name',
  };
  return map[id] || id;
}

function getProductDescKey(id: string) {
  return `prod_${id.replace('-', '_')}_desc`;
}

function getProductTagKeys(id: string) {
  const idNorm = id.replace('-', '_');
  return [`prod_${idNorm}_tag0`, `prod_${idNorm}_tag1`, `prod_${idNorm}_tag2`, `prod_${idNorm}_tag3`];
}

function getPipeSubNameKey(id: string) {
  return `pipe_${id}_name`;
}
function getPipeSubDescKey(id: string) {
  return `pipe_${id}_desc`;
}
function getPipeSubTagKeys(id: string) {
  return [`pipe_${id}_tag0`, `pipe_${id}_tag1`, `pipe_${id}_tag2`, `pipe_${id}_tag3`];
}

export default function ProductsSection() {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(productsStatic[0].id);
  const [activePipeSub, setActivePipeSub] = useState(pipeSubTypesStatic[0].id);
  const [showSpec, setShowSpec] = useState(false);

  const active = useMemo(() => productsStatic.find((p) => p.id === activeProduct) || productsStatic[0], [activeProduct]);
  const activeSub = useMemo(() => pipeSubTypesStatic.find((s) => s.id === activePipeSub) || pipeSubTypesStatic[0], [activePipeSub]);

  const displayId = active.hasSub ? activeSub.id : active.id;
  const displayName = active.hasSub ? t(getPipeSubNameKey(activeSub.id)) : t(getProductNameKey(active.id));
  const displayDesc = active.hasSub ? t(getPipeSubDescKey(activeSub.id)) : t(getProductDescKey(active.id));
  const displaySpec = active.hasSub ? activeSub.spec : active.spec;
  const displayImg = active.hasSub ? activeSub.img : active.img;
  const displayNameEn = active.hasSub ? activeSub.nameEn : active.nameEn;
  const displayTagKeys = active.hasSub ? getPipeSubTagKeys(activeSub.id) : getProductTagKeys(active.id);

  return (
    <section id="products" className="w-full bg-white py-16 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 md:mb-14">
          <div>
            <div className="text-[#E8762B] text-xs font-semibold tracking-widest mb-3">{t('products_section_label')}</div>
            <h2 className="text-3xl md:text-4xl font-black text-[#1C2E4A]" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              {t('products_title')}
            </h2>
          </div>
          <p className="text-[#6B7280] text-sm sm:text-right max-w-xs">
            {t('products_subtitle')}
          </p>
        </div>

        {/* Main Tab Selector */}
        <div className="flex items-center gap-0 mb-0 border-b border-gray-100 pb-0 overflow-x-auto">
          {productsStatic.map((p) => (
            <button
              key={p.id}
              onClick={() => setActiveProduct(p.id)}
              className={`px-3 md:px-5 py-3 text-xs md:text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
                activeProduct === p.id
                  ? 'border-[#E8762B] text-[#E8762B]'
                  : 'border-transparent text-[#6B7280] hover:text-[#1C2E4A]'
              }`}
            >
              {p.nameEn}
            </button>
          ))}
        </div>

        {/* Pipe Sub-Tabs */}
        {active.hasSub && (
          <div className="flex items-center gap-1 mt-4 mb-8 md:mb-10 bg-[#F4F6F8] rounded-xl p-1.5 w-full overflow-x-auto">
            {pipeSubTypesStatic.map((sub) => (
              <button
                key={sub.id}
                onClick={() => setActivePipeSub(sub.id)}
                className={`px-3 md:px-4 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 md:gap-1.5 ${
                  activePipeSub === sub.id
                    ? 'bg-white text-[#1C2E4A] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#1C2E4A]'
                }`}
              >
                {t(getPipeSubNameKey(sub.id))}
                <span className="text-[10px] text-[#E8762B] font-normal hidden sm:inline">{sub.nameEn}</span>
              </button>
            ))}
          </div>
        )}

        {!active.hasSub && <div className="mb-8 md:mb-10" />}

        {/* Active Product Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative h-[260px] md:h-[380px] lg:h-[480px] rounded-2xl overflow-hidden bg-[#F4F6F8]">
            <img
              key={displayId}
              src={displayImg}
              alt={displayName}
              className="w-full h-full object-cover object-center transition-opacity duration-300"
            />
            <div className="absolute top-4 left-4 bg-[#1C2E4A] text-white px-3 py-1.5 rounded-md text-xs font-bold tracking-widest">
              {displayNameEn}
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-2xl md:text-3xl font-black text-[#1C2E4A] mb-3 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              {displayName}
            </h3>
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <i className="ri-settings-3-fill text-[#E8762B] text-sm shrink-0"></i>
              <span className="text-xs md:text-sm text-[#6B7280] font-mono">{displaySpec}</span>
            </div>
            <p className="text-[#4A5568] leading-relaxed text-sm md:text-base mb-6 md:mb-8">
              {displayDesc}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-7 md:mb-10">
              {displayTagKeys.map((tagKey) => (
                <span key={tagKey} className="bg-[#F0F4FA] text-[#1C2E4A] px-3 py-1.5 rounded-full text-xs font-medium">
                  {t(tagKey)}
                </span>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowSpec(true)}
                className="flex items-center gap-2 bg-[#E8762B] hover:bg-[#D4661F] text-white px-5 md:px-6 py-2.5 md:py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                {t('products_spec_btn')}
              </button>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 bg-[#1C2E4A] hover:bg-[#263D5E] text-white px-5 md:px-6 py-2.5 md:py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                {t('products_quote_btn')}
              </a>
              <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                <i className="ri-phone-fill text-[#E8762B]"></i>
                <span>02-6941-1750</span>
              </div>
            </div>
          </div>
        </div>

        {/* All Products Grid Preview */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 md:gap-4 mt-10 md:mt-14">
          {productsStatic.map((p) => {
            const thumbImg = p.hasSub
              ? pipeSubTypesStatic.find((s) => s.id === activePipeSub)?.img || pipeSubTypesStatic[0].img
              : p.img;
            return (
              <button
                key={p.id}
                onClick={() => setActiveProduct(p.id)}
                className={`relative h-24 md:h-32 rounded-xl overflow-hidden cursor-pointer transition-all ${
                  activeProduct === p.id ? 'ring-2 ring-[#E8762B] ring-offset-2' : 'opacity-60 hover:opacity-90'
                }`}
              >
                <img src={thumbImg} alt={p.nameEn} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-2 left-0 right-0 text-center text-white text-[10px] md:text-xs font-bold">
                  {p.nameEn}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Spec Modal */}
      {showSpec && (
        <SpecModal
          productId={displayId}
          productName={displayName}
          onClose={() => setShowSpec(false)}
        />
      )}
    </section>
  );
}
